import Clickable from 'components/Common/Clickable'
import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'
import LineAngleRightIcon from 'LuxKit/Icons/line/LineAngleRightIcon'
import { setSvgIconSize } from 'LuxKit/SvgIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import LoyaltyBenefitIcon from 'loyalty/components/LoyaltyBenefitIcon'
import LoyaltyInfiniteBenefitIcon from 'loyalty/components/LoyaltyInfiniteBenefitIcon'
import useCurrentLoyaltyTier from 'loyalty/hooks/useCurrentLoyaltyTier'
import { rem } from 'polished'
import { useMemo, type ReactNode } from 'react'
import styled, { css } from 'styled-components'
import clsx from 'clsx'
import { tierGradientBackgroundStyle } from 'styles/utils/tierStyles'

type Layout = 'tile' | 'row'

const cardCss = css`
  width: 100%;
  text-align: left;
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  border: 1px solid ${props => props.theme.palette.neutral.default.six};
  border-radius: ${props => props.theme.borderRadius.L};
  display: flex;
  align-items: center;
  gap: ${rem(16)};
  padding: ${rem(16)};

  ${mediaQueryUp.tablet} {
    &.layout-tile {
      display: grid;
      grid-template-columns: 1fr auto;
      grid-template-areas:
        "badge   trailing"
        "content content";
      align-content: start;
      gap: ${rem(12)};
      min-height: ${rem(132)};
      padding: ${rem(20)};
    }
  }
`

const InteractiveCard = styled(Clickable)`
  ${cardCss}
  transition: background-color 0.15s ease;

  &:hover {
    background-color: ${props => props.theme.palette.neutral.default.seven};
  }
`

const StaticCard = styled.div`
  ${cardCss}
`

const Badge = styled.div`
  grid-area: badge;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${rem(32)};
  height: ${rem(32)};
  border-radius: 50%;
  ${setSvgIconSize('S')}

  ${mediaQueryUp.tablet} {
    &.layout-tile {
      width: ${rem(42)};
      height: ${rem(42)};
      ${setSvgIconSize('M')}
    }
  }

  ${tierGradientBackgroundStyle}
`

const Content = styled.div`
  grid-area: content;
  flex: 1;
  min-width: 0;
`

const Trailing = styled.div`
  grid-area: trailing;
  justify-self: end;
  flex-shrink: 0;
  display: flex;
  align-items: center;
`

interface Props {
  layout: Layout
  benefitType: App.LoyaltyBenefitType
  heading: ReactNode
  subtitle?: ReactNode
  /** Opens a details modal. */
  onClick?: () => void
  /** Navigates to a details page. */
  to?: string
  /** Replaces the chevron and makes the card non-interactive (e.g. a call button). */
  trailing?: ReactNode
}

function LandingPageLoyaltyBenefitTile({ layout, benefitType, heading, subtitle, onClick, to, trailing }: Props) {
  const [tier] = useCurrentLoyaltyTier()
  const isInteractive = !trailing && (!!onClick || !!to)

  const body = useMemo(() => {
    const trailingNode = trailing ?? (isInteractive ? <LineAngleRightIcon size={20} colour="neutral-three" /> : undefined)
    return <>
      <Badge className={clsx(`tier-${tier}`, `layout-${layout}`)}>
        {tier === 'infinite' && <LoyaltyInfiniteBenefitIcon benefitType={benefitType} />}
        {tier !== 'infinite' && <LoyaltyBenefitIcon benefitType={benefitType} colour={`tier-${tier}-contrast`} />}
      </Badge>
      <Content>
        <Group direction="vertical" gap={4}>
          <BodyText variant="large" weight="bold">{heading}</BodyText>
          {subtitle}
        </Group>
      </Content>
      {trailingNode && <Trailing>{trailingNode}</Trailing>}
    </>
  }, [tier, layout, benefitType, heading, subtitle, trailing, isInteractive])

  if (isInteractive) {
    return <InteractiveCard className={`layout-${layout}`} onClick={onClick} to={to}>{body}</InteractiveCard>
  }
  return <StaticCard className={`layout-${layout}`}>{body}</StaticCard>
}

export default LandingPageLoyaltyBenefitTile
